import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CampoDeMoeda,
  Aviso,
  Botao,
  Campo,
  Modal,
  Selo,
  Tabela,
  ValorFiscal,
  useDialogo,
  type ColunaTabela,
  type TomSemantico,
} from '../componentes/ui';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { requisicaoAutenticada } from '../servicos/http';
import { diaLocal, simOuNao, type ColunaDaPlanilha } from '../servicos/planilha';
import { ErroDaApi } from './ErroDaApi';
import { ContratoEmPdf } from './passivo/ContratoEmPdf';
import { humanizar, tomDoStatus, type ContratoPassivo } from './PassivoContratos';

export interface Parcela {
  readonly id: string;
  readonly numero: number;
  readonly dataVencimento: string;
  readonly valorParcela: number;
  readonly amortizacao: number;
  readonly juros: number;
  readonly saldoDevedor: number;
  readonly status: string;
  readonly diasDeAtraso: number;
  readonly valorPago: number | null;
  readonly dataPagamento: string | null;
  readonly comprovante: string | null;
  readonly confirmadoPorAssessor: boolean;
}

interface Checklist {
  readonly contratoId: string;
  readonly etapas: readonly {
    readonly id: string;
    readonly titulo: string;
    readonly concluida: boolean;
    readonly dataConclusao: string | null;
    readonly comprovanteUrl: string | null;
  }[];
  readonly percentualConcluido: number;
  readonly proximaEtapa: string | null;
  readonly statusGeral: string;
  readonly dataFinalizacao: string | null;
}

/**
 * Um contrato de Passivo por dentro: parcelas e checklist.
 *
 * ## Dois passos para uma parcela paga
 *
 * O cliente registra o pagamento com comprovante; o assessor **confirma**
 * depois (RN-241). São ações separadas porque quem paga e quem confere são
 * pessoas diferentes, e juntá-las faria o registro do cliente valer como
 * confirmação — a tela mostra as duas colunas para essa diferença ficar visível.
 *
 * ## O checklist não desfaz sozinho
 *
 * Anexar comprovante a uma etapa já concluída **não** a desmarca. Parece óbvio,
 * mas o legado fazia isso: mandava `concluida: false` junto com o arquivo e o
 * update sobrescrevia (dívida técnica #31). Aqui cada campo é enviado só quando
 * muda.
 */
/** O cronograma de pagamento, com o que já foi pago e conferido. */
export const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<Parcela>[] = [
  { titulo: 'Parcela', largura: 8, valor: (p) => p.numero },
  // A tela mostra o vencimento em UTC: é um dia, não um instante.
  { titulo: 'Vencimento', formato: 'data', valor: (p) => p.dataVencimento.slice(0, 10) },
  { titulo: 'Valor da parcela', formato: 'moeda', valor: (p) => p.valorParcela },
  { titulo: 'Amortização', formato: 'moeda', valor: (p) => p.amortizacao },
  { titulo: 'Juros', formato: 'moeda', valor: (p) => p.juros },
  { titulo: 'Saldo devedor', formato: 'moeda', valor: (p) => p.saldoDevedor },
  { titulo: 'Situação', largura: 16, valor: (p) => humanizar(p.status) },
  {
    titulo: 'Dias de atraso',
    largura: 10,
    valor: (p) => (p.diasDeAtraso > 0 ? p.diasDeAtraso : null),
  },
  { titulo: 'Valor pago', formato: 'moeda', valor: (p) => p.valorPago },
  { titulo: 'Pago em', formato: 'data', valor: (p) => diaLocal(p.dataPagamento) },
  {
    titulo: 'Conferido pelo assessor',
    largura: 12,
    valor: (p) => simOuNao(p.confirmadoPorAssessor),
  },
];

export function ParcelasDoContrato(props: {
  readonly contrato: ContratoPassivo | null;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  // `confirmar` já é a mutation que confirma parcela; o diálogo ganha outro nome.
  const { confirmar: perguntar } = useDialogo();
  const [pagando, setPagando] = useState<Parcela | null>(null);
  /** A data escolhida para uma etapa ainda não marcada — vale quando ela for marcada. */
  const [datasDasEtapas, setDatasDasEtapas] = useState<Readonly<Record<string, string>>>({});
  const id = props.contrato?.id ?? '';
  const hoje = new Date().toLocaleDateString('en-CA');

  const parcelas = useQuery({
    queryKey: ['passivo-parcelas', id],
    queryFn: () => requisicaoAutenticada<Parcela[]>(`/passivo/contratos/${id}/parcelas`),
    enabled: props.contrato !== null,
  });

  const checklist = useQuery({
    queryKey: ['passivo-checklist', id],
    queryFn: () => requisicaoAutenticada<Checklist>(`/passivo/contratos/${id}/checklist`),
    enabled: props.contrato !== null,
  });

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['passivo-parcelas', id] });
    void queryClient.invalidateQueries({ queryKey: ['passivo-checklist', id] });
    void queryClient.invalidateQueries({ queryKey: ['passivo-contratos'] });
    void queryClient.invalidateQueries({ queryKey: ['passivo-painel'] });
    void queryClient.invalidateQueries({ queryKey: ['passivo-simulacoes'] });
  };

  const mudarStatus = useMutation({
    mutationFn: (status: string) =>
      requisicaoAutenticada<unknown>(`/passivo/contratos/${id}/status`, {
        metodo: 'PATCH',
        corpo: { status },
      }),
    onSuccess: invalidar,
  });

  const confirmar = useMutation({
    mutationFn: (parcelaId: string) =>
      requisicaoAutenticada<unknown>(`/passivo/contratos/${id}/parcelas/${parcelaId}/confirmacao`, {
        metodo: 'POST',
        corpo: {},
      }),
    onSuccess: invalidar,
  });

  /**
   * Marcar, desmarcar ou corrigir a data de uma etapa. Marcada sem data, vale
   * o dia de hoje; com a data escolhida antes de marcar, vale ela.
   */
  const alterarEtapa = useMutation({
    mutationFn: (entrada: { etapaId: string; concluida?: boolean; dataConclusao?: string }) =>
      requisicaoAutenticada<unknown>(`/passivo/contratos/${id}/checklist/${entrada.etapaId}`, {
        metodo: 'PATCH',
        corpo: {
          ...(entrada.concluida === undefined ? {} : { concluida: entrada.concluida }),
          ...(entrada.dataConclusao === undefined ? {} : { dataConclusao: entrada.dataConclusao }),
        },
      }),
    onSuccess: (_r, entrada) => {
      setDatasDasEtapas(({ [entrada.etapaId]: _usada, ...resto }) => resto);
      invalidar();
    },
  });

  const colunas: readonly ColunaTabela<Parcela>[] = [
    {
      chave: 'numero',
      titulo: '#',
      numerica: true,
      larguraMinima: '3.5rem',
      celula: (p) => <span className="valor-fiscal text-sm">{p.numero}</span>,
    },
    {
      chave: 'dataVencimento',
      titulo: 'Vencimento',
      larguraMinima: '8rem',
      celula: (p) => (
        <span className="valor-fiscal whitespace-nowrap text-sm">
          {new Date(p.dataVencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
        </span>
      ),
    },
    {
      chave: 'valorParcela',
      titulo: 'Parcela',
      numerica: true,
      celula: (p) => <ValorFiscal valor={p.valorParcela} />,
    },
    {
      chave: 'juros',
      titulo: 'Juros',
      numerica: true,
      celula: (p) => <ValorFiscal valor={p.juros} tamanho="pequeno" />,
    },
    {
      chave: 'saldoDevedor',
      titulo: 'Saldo devedor',
      numerica: true,
      celula: (p) => <ValorFiscal valor={p.saldoDevedor} tamanho="pequeno" />,
    },
    {
      chave: 'status',
      titulo: 'Situação',
      larguraMinima: '8rem',
      celula: (p) => (
        <span className="flex flex-col gap-1">
          <Selo tom={tomDaParcela(p)}>{humanizar(p.status)}</Selo>
          {p.diasDeAtraso > 0 && (
            <span className="text-xs text-erro">{p.diasDeAtraso} dia(s) de atraso</span>
          )}
        </span>
      ),
    },
    {
      /*
        Registrar e confirmar numa coluna só, à vista: em duas colunas a tabela
        passava da largura da janela, e o "Registrar pagamento" ficava escondido
        à direita, só rolando de lado. Continuam dois passos (RN-241) — quem
        paga é o cliente, quem confere é o assessor ou a operação.
      */
      chave: 'pagamento',
      titulo: 'Pagamento',
      larguraMinima: '11rem',
      celula: (p) =>
        p.dataPagamento === null ? (
          <Botao variante="secundario" tamanho="pequeno" onClick={() => setPagando(p)}>
            Registrar pagamento
          </Botao>
        ) : (
          <span className="flex flex-col items-start gap-1">
            <span className="valor-fiscal text-xs text-conteudo-secundario">
              Pago em {new Date(p.dataPagamento).toLocaleDateString('pt-BR')}
            </span>
            {p.confirmadoPorAssessor ? (
              <Selo tom="positivo" tamanho="pequeno">
                Confirmada
              </Selo>
            ) : (
              <Botao
                variante="secundario"
                tamanho="pequeno"
                carregando={confirmar.isPending && confirmar.variables === p.id}
                onClick={() => confirmar.mutate(p.id)}
              >
                Confirmar
              </Botao>
            )}
          </span>
        ),
    },
  ];

  const c = props.contrato;

  return (
    <>
      <Modal
        aberto={c !== null}
        aoFechar={props.aoFechar}
        titulo="Contrato de Passivo"
        descricao={c === null ? undefined : `${c.quantidadeParcelas} parcelas`}
        largura="ampla"
        rodape={<Botao onClick={props.aoFechar}>Fechar</Botao>}
      >
        {c !== null && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-3">
              <Selo tom={tomDoStatus(c.status)}>{humanizar(c.status)}</Selo>
              {c.status === 'SIMULACAO' && (
                <Botao
                  tamanho="pequeno"
                  carregando={mudarStatus.isPending}
                  onClick={async () => {
                    const ok = await perguntar({
                      titulo: 'Ativar o contrato?',
                      descricao:
                        'O cronograma de parcelas é gerado e a edição do contrato trava a partir daqui.',
                      rotuloConfirmar: 'Ativar contrato',
                    });
                    if (ok) mudarStatus.mutate('ATIVO');
                  }}
                >
                  Ativar contrato
                </Botao>
              )}
              {c.status === 'ATIVO' && (
                <>
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    carregando={mudarStatus.isPending}
                    onClick={() => mudarStatus.mutate('FINALIZADO')}
                  >
                    Finalizar
                  </Botao>
                  <Botao
                    variante="perigo"
                    tamanho="pequeno"
                    carregando={mudarStatus.isPending}
                    onClick={async () => {
                      const ok = await perguntar({
                        titulo: 'Cancelar este contrato?',
                        descricao:
                          'O contrato deixa de valer e as parcelas em aberto param de ser cobradas. Não dá para reativar depois.',
                        rotuloConfirmar: 'Cancelar contrato',
                        rotuloCancelar: 'Voltar',
                        tom: 'perigo',
                      });
                      if (ok) mudarStatus.mutate('CANCELADO');
                    }}
                  >
                    Cancelar
                  </Botao>
                </>
              )}
            </div>

            {mudarStatus.isError && (
              <ErroDaApi titulo="Não foi possível mudar o status" erro={mudarStatus.error} />
            )}

            {/* ── Forma de pagamento ── */}
            <p className="text-sm text-conteudo-secundario">
              <span className="font-medium text-conteudo-primario">Forma de pagamento:</span>{' '}
              {c.regraPagamentoTexto === null ? (
                'padrão — parcelas mensais no dia do vencimento, a partir do mês seguinte à ativação.'
              ) : (
                <>
                  “{c.regraPagamentoTexto}”
                  {c.regraPagamentoDescricao !== null && ` — ${c.regraPagamentoDescricao}`}
                </>
              )}
            </p>

            {/* ── O contrato em PDF (D-020) ── */}
            <ContratoEmPdf contratoId={c.id} cancelado={c.status === 'CANCELADO'} />

            {/* ── Checklist ── */}
            {checklist.data !== undefined && checklist.data.etapas.length > 0 && (
              <section className="flex flex-col gap-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-conteudo-primario">
                    Checklist operacional
                  </h3>
                  <span className="text-xs text-conteudo-sutil">
                    {checklist.data.percentualConcluido}% concluído
                  </span>
                </div>
                <p className="text-xs text-conteudo-sutil">
                  Marque cada etapa com a data em que foi cumprida — em branco, vale o dia em que
                  você marcar. A data de uma etapa marcada se corrige no próprio campo.
                </p>
                <ul className="flex flex-col gap-1">
                  {checklist.data.etapas.map((e) => {
                    const dataGravada = e.dataConclusao?.slice(0, 10) ?? '';
                    return (
                      <li
                        key={e.id}
                        className="flex flex-wrap items-center gap-3 rounded-card border border-borda px-3 py-2"
                      >
                        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                          <input
                            type="checkbox"
                            checked={e.concluida}
                            disabled={alterarEtapa.isPending}
                            onChange={(ev) =>
                              alterarEtapa.mutate({
                                etapaId: e.id,
                                concluida: ev.target.checked,
                                // Marcando: a data escolhida antes, se houver.
                                ...(ev.target.checked && (datasDasEtapas[e.id] ?? '') !== ''
                                  ? { dataConclusao: datasDasEtapas[e.id] }
                                  : {}),
                              })
                            }
                            className="caixa-de-marcar"
                          />
                          <span className="text-sm text-conteudo-primario">{e.titulo}</span>
                        </label>
                        <input
                          type="date"
                          max={hoje}
                          aria-label={`Data em que "${e.titulo}" foi cumprida`}
                          title={
                            e.concluida
                              ? 'Data em que foi cumprida'
                              : 'Opcional: a data em que foi cumprida. Em branco, vale o dia em que marcar.'
                          }
                          disabled={alterarEtapa.isPending}
                          value={datasDasEtapas[e.id] ?? (e.concluida ? dataGravada : '')}
                          onChange={(ev) =>
                            setDatasDasEtapas((atual) => ({ ...atual, [e.id]: ev.target.value }))
                          }
                          // Na etapa marcada, a data corrigida grava ao sair do campo: digitando
                          // dia a dia, cada tecla seria uma gravação de uma data pela metade.
                          onBlur={() => {
                            const data = datasDasEtapas[e.id];
                            if (
                              e.concluida &&
                              data !== undefined &&
                              data !== '' &&
                              data !== dataGravada
                            ) {
                              alterarEtapa.mutate({ etapaId: e.id, dataConclusao: data });
                            }
                          }}
                          className="h-9 rounded-card border border-borda bg-superficie-base px-2 text-sm text-conteudo-primario"
                        />
                      </li>
                    );
                  })}
                </ul>
                {alterarEtapa.isError && (
                  <ErroDaApi titulo="Não foi possível alterar" erro={alterarEtapa.error} />
                )}
              </section>
            )}

            {/* ── Parcelas ── */}
            <section className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-conteudo-primario">
                  Parcelas ({parcelas.data?.length ?? 0})
                </h3>
                {(parcelas.data ?? []).length > 0 && (
                  <BotaoExtrairXlsx
                    titulo="Parcelas do contrato - Passivo"
                    tamanho="pequeno"
                    colunas={COLUNAS_DA_PLANILHA}
                    linhas={parcelas.data ?? []}
                  />
                )}
              </div>

              {(parcelas.data ?? []).length === 0 ? (
                <p className="text-sm text-conteudo-sutil">
                  {c.regraPagamentoTexto !== null && c.status !== 'CANCELADO'
                    ? 'As parcelas nascem quando a etapa "Transferência do crédito realizada" for marcada no checklist — os vencimentos contam da data dela.'
                    : c.status === 'SIMULACAO'
                      ? 'O cronograma é gerado quando o contrato é ativado.'
                      : 'Nenhuma parcela gerada.'}
                </p>
              ) : (
                <Tabela
                  legenda="Parcelas do contrato"
                  densidade="compacta"
                  colunas={colunas}
                  dados={parcelas.data ?? []}
                  chaveDaLinha={(p) => p.id}
                  chaveDeCabecalhoDaLinha="numero"
                  carregando={parcelas.isPending}
                />
              )}
            </section>
          </div>
        )}
      </Modal>

      <ModalDePagamento
        contratoId={id}
        parcela={pagando}
        aoFechar={() => setPagando(null)}
        aoPagar={invalidar}
      />
    </>
  );
}

/* ─── Pagamento ───────────────────────────────────────────────────────────── */

export function ModalDePagamento(props: {
  readonly contratoId: string;
  readonly parcela: Parcela | null;
  readonly aoFechar: () => void;
  readonly aoPagar: () => void;
}): JSX.Element {
  const [valor, setValor] = useState('');
  const [data, setData] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);

  const pagar = useMutation({
    mutationFn: async () => {
      const comprovante =
        arquivo === null
          ? undefined
          : {
              nome: arquivo.name,
              conteudoBase64: await lerComoBase64(arquivo),
              ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
            };

      return requisicaoAutenticada<unknown>(
        `/passivo/contratos/${props.contratoId}/parcelas/${props.parcela?.id ?? ''}/pagamento`,
        {
          metodo: 'POST',
          corpo: {
            ...(valor === ''
              ? {}
              : { valorPago: Number(valor.replace(/\./g, '').replace(',', '.')) }),
            ...(data === '' ? {} : { dataPagamento: `${data}T12:00:00.000Z` }),
            ...(comprovante === undefined ? {} : { comprovante }),
          },
        },
      );
    },
    onSuccess: () => {
      props.aoPagar();
      fechar();
    },
  });

  const fechar = (): void => {
    pagar.reset();
    setValor('');
    setData('');
    setArquivo(null);
    props.aoFechar();
  };

  const p = props.parcela;

  return (
    <Modal
      aberto={p !== null}
      aoFechar={fechar}
      titulo="Registrar pagamento"
      descricao={p === null ? undefined : `Parcela ${p.numero}`}
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => pagar.mutate()} carregando={pagar.isPending}>
            Registrar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <CampoDeMoeda
          rotulo="Valor pago"
          ajuda={
            p === null
              ? undefined
              : `Vazio usa o valor da parcela: ${p.valorParcela.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}.`
          }
          value={valor}
          onChange={setValor}
        />
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-primario">Data do pagamento</span>
          <input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="h-11 rounded-card border border-borda bg-superficie-base px-3 text-sm text-conteudo-primario"
          />
          <span className="text-xs text-conteudo-sutil">Vazio usa hoje.</span>
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-primario">Comprovante</span>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
          />
        </label>

        <Aviso tom="informacao">
          Registrar não é confirmar: o pagamento fica aguardando a conferência do assessor (RN-241).
        </Aviso>

        {pagar.isError && <ErroDaApi titulo="Não foi possível registrar" erro={pagar.error} />}
      </div>
    </Modal>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

export function tomDaParcela(p: Parcela): TomSemantico {
  if (p.confirmadoPorAssessor) return 'positivo';
  if (p.diasDeAtraso > 0) return 'critico';
  if (p.dataPagamento !== null) return 'atencao';
  return 'neutro';
}

/** `FileReader` devolve `data:...;base64,XXX` — só o depois da vírgula interessa. */
async function lerComoBase64(arquivo: File): Promise<string> {
  const leitor = new FileReader();
  return new Promise((resolver, rejeitar) => {
    leitor.onload = () => {
      const resultado = String(leitor.result);
      resolver(resultado.slice(resultado.indexOf(',') + 1));
    };
    leitor.onerror = () => rejeitar(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}
